import React, { useState, useEffect } from 'react';
import { TrendingUp, TrendingDown, RefreshCw } from 'lucide-react';

const WORKER_URL = 'https://tickertapapi.avetter90.workers.dev';

const MetalsTracker = () => {
  const [metals, setMetals] = useState(null);
  const [comex, setComex] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);

  const fetchMetals = async () => {
    try {
      setRefreshing(true);
      const pricesResp = await fetch(`${WORKER_URL}/api/metals/prices`);
      if (!pricesResp.ok) throw new Error('Failed to fetch metals data');
      const data = await pricesResp.json();
      setMetals(data);
      setLastUpdated(new Date());
    } catch (error) {
      console.error('Error fetching metals:', error);
    } finally {
      setRefreshing(false);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMetals();
    const interval = setInterval(fetchMetals, 60000);
    return () => clearInterval(interval);
  }, []);

  const tickerItems = metals ? [
    { label: 'GOLD', price: metals.gold?.price, change: metals.gold?.change },
    { label: 'SILVER', price: metals.silver?.price, change: metals.silver?.change },
    { label: 'PLATINUM', price: metals.platinum?.price, change: metals.platinum?.change },
    { label: 'PALLADIUM', price: metals.palladium?.price, change: metals.palladium?.change },
    { label: 'COPPER', price: metals.copper?.price, change: metals.copper?.change },
    { label: 'BITCOIN', price: metals.bitcoin?.price, change: metals.bitcoin?.change },
    { label: 'GSR', price: metals.goldSilverRatio, change: null, isRatio: true },
  ] : [];

  const tableRows = [
    { rank: 1, name: 'Gold', symbol: 'XAU', emoji: '🟡', key: 'gold' },
    { rank: 2, name: 'Silver', symbol: 'XAG', emoji: '⚪', key: 'silver' },
    { rank: 3, name: 'Platinum', symbol: 'XPT', emoji: '🔘', key: 'platinum' },
    { rank: 4, name: 'Palladium', symbol: 'XPD', emoji: '⬛', key: 'palladium' },
    { rank: 5, name: 'Copper', symbol: 'HG', emoji: '🟠', key: 'copper' },
    { rank: 6, name: 'Bitcoin', symbol: 'BTC', emoji: '₿', key: 'bitcoin' },
  ];

  const formatPrice = (price, key) => {
    if (!price) return '$0.00';
    if (key === 'copper') return `$${price.toFixed(4)}`;
    if (key === 'bitcoin') return `$${price.toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
    return `$${price.toFixed(2)}`;
  };

  const formatChange = (change) => {
    if (change === null || change === undefined) return null;
    const sign = change >= 0 ? '+' : '';
    return `${sign}${change.toFixed(2)}%`;
  };

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', background: '#0a0a0a', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ color: '#94a3b8' }}>Loading metals data...</div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: '#0a0a0a', color: 'white' }}>

      {/* Scrolling Ticker */}
      <div style={{ background: '#0f0f0f', borderBottom: '1px solid #1a1a1a', overflow: 'hidden' }}>
        <style>{`
          @keyframes tickerScroll {
            0% { transform: translateX(0); }
            100% { transform: translateX(-33.33%); }
          }
          .metals-ticker {
            display: flex;
            animation: tickerScroll 60s linear infinite;
            width: max-content;
          }
          .metals-ticker:hover { animation-play-state: paused; }
        `}</style>
        <div className="metals-ticker">
          {[...tickerItems, ...tickerItems, ...tickerItems].map((item, idx) => (
            <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 16px', borderRight: '1px solid #1a1a1a', whiteSpace: 'nowrap' }}>
              <span style={{ color: '#94a3b8', fontWeight: 600, fontSize: '13px' }}>{item.label}</span>
              <span style={{ color: 'white', fontWeight: 700, fontSize: '13px' }}>
                {item.isRatio
                  ? (item.price ? item.price.toFixed(1) : '—')
                  : formatPrice(item.price, item.label.toLowerCase())}
              </span>
              {item.change !== null && item.change !== undefined && (
                <span style={{ fontSize: '12px', fontWeight: 600, color: item.change >= 0 ? '#10b981' : '#ef4444' }}>
                  {formatChange(item.change)}
                </span>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Top Header */}
      <div style={{ height: '64px', borderBottom: '1px solid #1a1a1a', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 32px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <span style={{ fontWeight: 700, fontSize: '20px', background: 'linear-gradient(to right, #60a5fa, #3b82f6)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>TickerTap</span>
          <span style={{ color: '#1a1a1a' }}>|</span>
          <span style={{ color: '#94a3b8', fontSize: '14px' }}>Live Metals Tracker</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '24px' }}>
          {lastUpdated && (
            <span style={{ color: '#64748b', fontSize: '12px' }}>Updated {lastUpdated.toLocaleTimeString()}</span>
          )}
          <button
            onClick={fetchMetals}
            disabled={refreshing}
            style={{ background: 'none', border: 'none', color: refreshing ? '#64748b' : '#94a3b8', cursor: 'pointer', fontSize: '14px', display: 'flex', alignItems: 'center', gap: '6px' }}
          >
            <RefreshCw size={14} />
            {refreshing ? 'Refreshing...' : 'Refresh Data'}
          </button>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: '11px', color: '#64748b' }}>Gold/Silver Ratio</div>
            <div style={{ fontSize: '16px', fontWeight: 700 }}>{metals?.goldSilverRatio?.toFixed(1) || '—'}</div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: '11px', color: '#64748b' }}>BTC/Silver Ratio</div>
            <div style={{ fontSize: '16px', fontWeight: 700 }}>{metals?.btcSilverRatio?.toFixed(0) || '—'}</div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div style={{ maxWidth: '1800px', margin: '0 auto', padding: '32px' }}>

        {/* Metals Table */}
        <div style={{ background: '#0f0f0f', border: '1px solid #1a1a1a', borderRadius: '8px', overflow: 'hidden', marginBottom: '32px' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid #1a1a1a' }}>
                <th style={{ textAlign: 'left', padding: '16px 24px', fontSize: '11px', fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>#</th>
                <th style={{ textAlign: 'left', padding: '16px 24px', fontSize: '11px', fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Asset</th>
                <th style={{ textAlign: 'right', padding: '16px 24px', fontSize: '11px', fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Price</th>
                <th style={{ textAlign: 'right', padding: '16px 24px', fontSize: '11px', fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>24h Change</th>
                <th style={{ textAlign: 'right', padding: '16px 24px', fontSize: '11px', fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>YTD</th>
                <th style={{ textAlign: 'right', padding: '16px 24px', fontSize: '11px', fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>52W High</th>
                <th style={{ textAlign: 'right', padding: '16px 24px', fontSize: '11px', fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>52W Low</th>
                <th style={{ textAlign: 'right', padding: '16px 24px', fontSize: '11px', fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>vs Gold</th>
                <th style={{ textAlign: 'right', padding: '16px 24px', fontSize: '11px', fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>vs Bitcoin</th>
              </tr>
            </thead>
            <tbody>
              {tableRows.map((row) => {
                const data = metals?.[row.key];
                const price = data?.price || 0;
                const change = data?.change || 0;
                const vsGold = row.key !== 'gold' && metals?.gold?.price && price
                  ? (metals.gold.price / price).toFixed(2) : null;
                const vsBtc = row.key !== 'bitcoin' && metals?.bitcoin?.price && price
                  ? (metals.bitcoin.price / price).toFixed(0) : null;

                return (
                  <tr
                    key={row.key}
                    style={{ borderBottom: '1px solid #1a1a1a' }}
                    onMouseEnter={e => e.currentTarget.style.background = '#141414'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                  >
                    <td style={{ padding: '16px 24px', color: '#64748b', fontSize: '14px' }}>{row.rank}</td>
                    <td style={{ padding: '16px 24px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <span style={{ fontSize: '20px' }}>{row.emoji}</span>
                        <div>
                          <div style={{ fontFamily: 'monospace', fontWeight: 700, color: 'white', fontSize: '14px' }}>{row.name}</div>
                          <div style={{ fontSize: '11px', color: '#64748b' }}>{row.symbol}</div>
                        </div>
                      </div>
                    </td>
                    <td style={{ padding: '16px 24px', textAlign: 'right', fontWeight: 700, fontSize: '14px', color: 'white' }}>
                      {formatPrice(price, row.key)}
                    </td>
                    <td style={{ padding: '16px 24px', textAlign: 'right' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '4px' }}>
                        {change >= 0 ? <TrendingUp size={14} color="#10b981" /> : <TrendingDown size={14} color="#ef4444" />}
                        <span style={{ fontWeight: 600, fontSize: '14px', color: change >= 0 ? '#10b981' : '#ef4444' }}>
                          {formatChange(change)}
                        </span>
                      </div>
                    </td>
                    <td style={{ padding: '16px 24px', textAlign: 'right' }}>
                      {data?.ytd !== undefined && data?.ytd !== null
                        ? <span style={{ fontSize: '13px', fontWeight: 600, color: data.ytd >= 0 ? '#10b981' : '#ef4444' }}>{data.ytd >= 0 ? '+' : ''}{data.ytd.toFixed(2)}%</span>
                        : <span style={{ color: '#64748b', fontSize: '13px' }}>—</span>}
                    </td>
                    <td style={{ padding: '16px 24px', textAlign: 'right', fontSize: '13px', color: '#94a3b8' }}>
                      {data?.week52High ? formatPrice(data.week52High, row.key) : '—'}
                    </td>
                    <td style={{ padding: '16px 24px', textAlign: 'right', fontSize: '13px', color: '#94a3b8' }}>
                      {data?.week52Low ? formatPrice(data.week52Low, row.key) : '—'}
                    </td>
                    <td style={{ padding: '16px 24px', textAlign: 'right', fontSize: '13px', color: '#94a3b8' }}>
                      {vsGold ? `${vsGold}x` : '—'}
                    </td>
                    <td style={{ padding: '16px 24px', textAlign: 'right', fontSize: '13px', color: '#94a3b8' }}>
                      {vsBtc ? `${Number(vsBtc).toLocaleString()}x` : '—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Bottom Row */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', marginBottom: '24px' }}>

          {/* GSR Widget */}
          <div style={{ background: '#0f0f0f', border: '1px solid #1a1a1a', borderRadius: '8px', padding: '24px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
              <div style={{ fontSize: '11px', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Gold / Silver Ratio</div>
              <div style={{ fontSize: '11px', color: '#64748b' }}>avg ~60:1 · live via TradingView</div>
            </div>
            <div style={{ borderRadius: '6px', overflow: 'hidden', marginBottom: '12px' }}>
              <iframe
                src="https://s.tradingview.com/widgetembed/?symbol=TVC%3AGOLD%2FTVC%3ASILVER&interval=D&theme=dark&style=3&hide_top_toolbar=1&hide_side_toolbar=1&hide_legend=1&hide_volume=1&no_referral_id=1&locale=en"
                style={{ width: '100%', height: '160px', border: 'none', background: '#0f0f0f', borderRadius: '6px' }}
                title="GSR Chart"
              />
            </div>
            <div style={{ fontSize: '13px' }}>
              {metals?.goldSilverRatio > 80
                ? <span style={{ color: '#10b981' }}>▲ Silver undervalued vs gold</span>
                : metals?.goldSilverRatio < 50
                ? <span style={{ color: '#f59e0b' }}>▼ Silver overvalued vs gold</span>
                : <span style={{ color: '#94a3b8' }}>Near historical average</span>}
            </div>
          </div>

          {/* Stack Calculator */}
          <div style={{ background: '#0f0f0f', border: '1px solid #1a1a1a', borderRadius: '8px', padding: '24px' }}>
            <div style={{ fontSize: '11px', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '16px' }}>Stack Calculator</div>
            <StackCalculator silverPrice={metals?.silver?.price || 0} goldPrice={metals?.gold?.price || 0} />
          </div>

        </div>

        {/* Bottom Row - 2x2 Grid */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
          <ComexWidget />
          <ShanghaiInventoryWidget />
          <SupplyDemandWidget />
          <PremiumMetricsWidget />
        </div>

        {/* Performance Chart - Full Width */}
        <div style={{ marginTop: '16px' }}>
          <PerformanceComparisonWidget metals={metals} />
        </div>

      </div>
    </div>
  );
};

// ============================================
// COMEX WIDGET - Historical chart + live stats
// ============================================
// Supply/Demand data from Silver Institute World Silver Survey
const SUPPLY_DEMAND_DATA = [
  { year: 2016, supply: 986.5, demand: 1033.9, deficit: 47.4 },
  { year: 2017, supply: 1025.3, demand: 1034.2, deficit: 8.9 },
  { year: 2018, supply: 1033.7, demand: 1016.0, deficit: -17.7 },
  { year: 2019, supply: 1047.9, demand: 991.8, deficit: -56.1 },
  { year: 2020, supply: 1002.1, demand: 896.1, deficit: -106.0 },
  { year: 2021, supply: 1049.0, demand: 1204.5, deficit: 155.5 },
  { year: 2022, supply: 1029.4, demand: 1242.6, deficit: 213.2 },
  { year: 2023, supply: 1017.5, demand: 1178.5, deficit: 161.0 },
  { year: 2024, supply: 1013.6, demand: 1162.5, deficit: 148.9 },
  { year: 2025, supply: 1029.0, demand: 1146.6, deficit: 117.6 },
];

// COMEX Silver inventory — registered + eligible, monthly from 2016
const COMEX_BASELINE = [
  { date: '2016-01-01', registered: 155.2, eligible: 22.1, total: 177.3 },
  { date: '2016-07-01', registered: 148.6, eligible: 30.4, total: 179.0 },
  { date: '2017-01-01', registered: 161.3, eligible: 38.2, total: 199.5 },
  { date: '2017-07-01', registered: 172.8, eligible: 45.6, total: 218.4 },
  { date: '2018-01-01', registered: 182.4, eligible: 52.3, total: 234.7 },
  { date: '2018-07-01', registered: 191.6, eligible: 58.9, total: 250.5 },
  { date: '2019-01-01', registered: 198.3, eligible: 67.4, total: 265.7 },
  { date: '2019-07-01', registered: 204.7, eligible: 74.8, total: 279.5 },
  { date: '2020-01-01', registered: 215.4, eligible: 88.2, total: 303.6 },
  { date: '2020-07-01', registered: 241.8, eligible: 118.6, total: 360.4 },
  { date: '2021-01-01', registered: 268.4, eligible: 128.3, total: 396.7 },
  { date: '2021-04-01', registered: 278.6, eligible: 124.1, total: 402.7 },
  { date: '2021-07-01', registered: 254.3, eligible: 118.7, total: 373.0 },
  { date: '2022-01-01', registered: 238.7, eligible: 112.4, total: 351.1 },
  { date: '2022-07-01', registered: 221.4, eligible: 108.6, total: 330.0 },
  { date: '2023-01-01', registered: 198.6, eligible: 98.3, total: 296.9 },
  { date: '2023-07-01', registered: 168.4, eligible: 94.2, total: 262.6 },
  { date: '2024-01-01', registered: 152.8, eligible: 138.4, total: 291.2 },
  { date: '2024-04-01', registered: 134.2, eligible: 212.6, total: 346.8 },
  { date: '2024-07-01', registered: 68.3,  eligible: 272.4, total: 340.7 },
  { date: '2024-10-01', registered: 31.2,  eligible: 279.8, total: 311.0 },
  { date: '2025-01-01', registered: 73.1,  eligible: 265.2, total: 338.3 },
  { date: '2025-04-01', registered: 158.3, eligible: 241.5, total: 399.8 },
  { date: '2025-07-01', registered: 165.8, eligible: 236.4, total: 402.2 },
  { date: '2025-10-01', registered: 167.7, eligible: 232.6, total: 400.3 },
  { date: '2025-11-26', registered: 142.7, eligible: 241.1, total: 383.8 },
  { date: '2025-12-01', registered: 138.4, eligible: 243.7, total: 382.1 },
  { date: '2026-01-07', registered: 113.3, eligible: 258.6, total: 371.9 },
  { date: '2026-02-05', registered: 103.5, eligible: 271.8, total: 375.3 },
  { date: '2026-02-13', registered: 93.0,  eligible: 286.2, total: 379.2 },
];

const TIMEFRAMES = [
  { label: '1W',  days: 7 },
  { label: '1M',  days: 30 },
  { label: '3M',  days: 90 },
  { label: '6M',  days: 180 },
  { label: '1Y',  days: 365 },
  { label: 'ALL', days: 9999 },
];

const ComexWidget = () => {
  const [timeframe, setTimeframe] = useState('ALL');
  const [liveHistory, setLiveHistory] = useState([]);
  const [hoveredPoint, setHoveredPoint] = useState(null);

  useEffect(() => {
    fetch(`${WORKER_URL}/api/metals/comex-history`)
      .then(r => r.json())
      .then(d => {
        if (d.history) {
          // Convert oz to millions for chart consistency
          setLiveHistory(d.history.map(h => ({
            date: h.date,
            registered: h.registered / 1e6,
            eligible: h.eligible / 1e6,
            total: h.total / 1e6,
            live: true
          })));
        }
      })
      .catch(() => {});
  }, []);

  // Merge baseline + live, deduplicate by date (live wins)
  const liveByDate = Object.fromEntries(liveHistory.map(d => [d.date, d]));
  const merged = [
    ...COMEX_BASELINE.filter(d => !liveByDate[d.date]),
    ...liveHistory
  ].sort((a, b) => a.date.localeCompare(b.date));

  // Filter by timeframe
  const cutoff = new Date();
  const tf = TIMEFRAMES.find(t => t.label === timeframe);
  cutoff.setDate(cutoff.getDate() - (tf?.days || 9999));
  const visible = merged.filter(d => tf?.days === 9999 || new Date(d.date) >= cutoff);

  // Chart dimensions
  const W = 700, H = 200, PL = 44, PR = 16, PT = 8, PB = 20;
  const chartW = W - PL - PR;
  const chartH = H - PT - PB;

  const allVals = visible.flatMap(d => [d.registered, d.eligible]);
  const rawMin = Math.floor(Math.min(...allVals) * 0.92 / 10) * 10;
  const rawMax = Math.ceil(Math.max(...allVals) * 1.05 / 10) * 10;

  // Y-axis — fixed 50M increments
  const yStep = 50;
  const yMin = Math.floor(rawMin / yStep) * yStep;
  const yMax = Math.ceil(rawMax / yStep) * yStep;
  const yTicks = [];
  for (let v = yMin; v <= yMax; v += yStep) yTicks.push(v);

  const xScale = (i) => PL + (i / Math.max(visible.length - 1, 1)) * chartW;
  const yScale = (v) => PT + chartH - ((v - yMin) / (yMax - yMin)) * chartH;

  const registeredPoints = visible.map((d, i) => `${xScale(i)},${yScale(d.registered)}`).join(' ');
  const eligiblePoints   = visible.map((d, i) => `${xScale(i)},${yScale(d.eligible)}`).join(' ');

  // Area path for registered (filled under)
  const regPath = visible.length > 0
    ? `M${xScale(0)},${yScale(visible[0].registered)} ` +
      visible.map((d, i) => `L${xScale(i)},${yScale(d.registered)}`).join(' ') +
      ` L${xScale(visible.length - 1)},${PT + chartH} L${xScale(0)},${PT + chartH} Z`
    : '';

  // X-axis — one label per year (Jan 1 entries)
  const yearLabels = visible.reduce((acc, d, i) => {
    const yr = d.date.slice(0, 4);
    if (!acc.find(a => a.yr === yr)) acc.push({ yr, i });
    return acc;
  }, []);

  const fmtDate = (dateStr) => {
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
  };

  const latest = visible[visible.length - 1];
  const prev   = visible[0];
  const regChange = prev && latest ? latest.registered - prev.registered : 0;
  const regChangePct = prev?.registered ? (regChange / prev.registered) * 100 : 0;

  // Use hovered point data if hovering, otherwise latest
  const displayPoint = hoveredPoint !== null ? visible[hoveredPoint] : latest;

  const tfBtnStyle = (active) => ({
    padding: '4px 10px',
    background: active ? '#1a1a1a' : 'transparent',
    border: `1px solid ${active ? '#2a2a2a' : 'transparent'}`,
    borderRadius: '4px',
    color: active ? 'white' : '#64748b',
    fontSize: '11px',
    fontWeight: active ? 600 : 400,
    cursor: 'pointer'
  });

  return (
    <div style={{ background: '#0f0f0f', border: '1px solid #1a1a1a', borderRadius: '8px', padding: '24px' }}>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '16px' }}>
        <div>
          <div style={{ fontSize: '11px', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>COMEX Silver Inventory</div>
          <div style={{ fontSize: '11px', color: '#64748b', marginTop: '2px' }}>
            Registered (deliverable) vs Eligible · oz in millions
          </div>
        </div>
        <a href="https://www.cmegroup.com/delivery_reports/Silver_stocks.xls"
           target="_blank" rel="noopener noreferrer"
           style={{ fontSize: '11px', color: '#3b82f6', textDecoration: 'none' }}>Live Report ↗</a>
      </div>

      {/* Stat Cards - compact, interactive */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr 1fr', gap: '8px', marginBottom: '12px' }}>
        <div style={{ background: '#0a0a0a', borderRadius: '4px', padding: '8px 10px' }}>
          <div style={{ fontSize: '9px', color: '#64748b', marginBottom: '2px' }}>
            REGISTERED {hoveredPoint !== null && <span style={{ color: '#3b82f6' }}>({displayPoint.date})</span>}
          </div>
          <div style={{ fontSize: '15px', fontWeight: 700, color: '#3b82f6' }}>
            {displayPoint ? displayPoint.registered.toFixed(1) : '—'}M
          </div>
          {hoveredPoint === null && (
            <div style={{ fontSize: '10px', color: regChange < 0 ? '#ef4444' : '#10b981' }}>
              {regChange >= 0 ? '+' : ''}{regChange.toFixed(1)}M {timeframe}
            </div>
          )}
        </div>
        <div style={{ background: '#0a0a0a', borderRadius: '4px', padding: '8px 10px' }}>
          <div style={{ fontSize: '9px', color: '#64748b', marginBottom: '2px' }}>ELIGIBLE</div>
          <div style={{ fontSize: '15px', fontWeight: 700, color: '#94a3b8' }}>
            {displayPoint ? displayPoint.eligible.toFixed(1) : '—'}M
          </div>
        </div>
        <div style={{ background: '#0a0a0a', borderRadius: '4px', padding: '8px 10px' }}>
          <div style={{ fontSize: '9px', color: '#64748b', marginBottom: '2px' }}>TOTAL</div>
          <div style={{ fontSize: '15px', fontWeight: 700, color: 'white' }}>
            {displayPoint ? displayPoint.total.toFixed(1) : '—'}M
          </div>
        </div>
        <div style={{ background: '#0a0a0a', borderRadius: '4px', padding: '8px 10px' }}>
          <div style={{ fontSize: '9px', color: '#64748b', marginBottom: '2px' }}>REG %</div>
          <div style={{ fontSize: '15px', fontWeight: 700, color: displayPoint && displayPoint.registered / displayPoint.total < 0.25 ? '#ef4444' : displayPoint.registered / displayPoint.total < 0.30 ? '#f59e0b' : '#10b981' }}>
            {displayPoint ? ((displayPoint.registered / displayPoint.total) * 100).toFixed(1) : '—'}%
          </div>
        </div>
        <div style={{ background: '#0a0a0a', borderRadius: '4px', padding: '8px 10px' }}>
          <div style={{ fontSize: '9px', color: '#64748b', marginBottom: '2px' }}>PEAK DROP</div>
          <div style={{ fontSize: '15px', fontWeight: 700, color: '#ef4444' }}>
            {latest ? ((1 - latest.registered / 167.7) * 100).toFixed(0) : '—'}%
          </div>
        </div>
      </div>

      {/* Timeframe Buttons */}
      <div style={{ display: 'flex', gap: '4px', marginBottom: '12px' }}>
        {TIMEFRAMES.map(t => (
          <button key={t.label} style={tfBtnStyle(timeframe === t.label)} onClick={() => setTimeframe(t.label)}>
            {t.label}
          </button>
        ))}
        <div style={{ marginLeft: 'auto', fontSize: '11px', color: '#64748b', display: 'flex', alignItems: 'center', gap: '12px' }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <span style={{ width: 10, height: 2, background: '#3b82f6', display: 'inline-block', borderRadius: 1 }}></span> Registered
          </span>
          <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <span style={{ width: 10, height: 2, background: '#475569', display: 'inline-block', borderRadius: 1, borderTop: '1px dashed #475569' }}></span> Eligible
          </span>
        </div>
      </div>

      {/* SVG Chart */}
      {visible.length > 1 ? (
        <svg 
          width="100%" 
          viewBox={`0 0 ${W} ${H}`} 
          style={{ display: 'block' }}
          onMouseLeave={() => setHoveredPoint(null)}
        >
          <defs>
            <linearGradient id="regGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#3b82f6" stopOpacity="0.3" />
              <stop offset="100%" stopColor="#3b82f6" stopOpacity="0.02" />
            </linearGradient>
          </defs>

          {/* Y grid lines */}
          {yTicks.map(v => (
            <g key={v}>
              <line x1={PL} y1={yScale(v)} x2={W - PR} y2={yScale(v)}
                stroke="#1a1a1a" strokeWidth="1" />
              <text x={PL - 6} y={yScale(v) + 4} textAnchor="end"
                fontSize="9" fill="#475569">{v}M</text>
            </g>
          ))}

          {/* Area fill — registered */}
          <path d={regPath} fill="url(#regGradient)" />

          {/* Eligible line - dashed gray */}
          <polyline points={eligiblePoints} fill="none" stroke="#475569" strokeWidth="1.5" strokeDasharray="4 3" />

          {/* Registered line - blue */}
          <polyline points={registeredPoints} fill="none" stroke="#3b82f6" strokeWidth="2" />

          {/* Interactive hover area - full width vertical slices */}
          {visible.map((d, i) => {
            const nextI = i + 1 < visible.length ? i + 1 : i;
            const x1 = i === 0 ? PL : (xScale(i) + xScale(i - 1)) / 2;
            const x2 = i === visible.length - 1 ? W - PR : (xScale(i) + xScale(nextI)) / 2;
            return (
              <rect
                key={`hover-${i}`}
                x={x1}
                y={PT}
                width={x2 - x1}
                height={chartH}
                fill="transparent"
                onMouseEnter={() => setHoveredPoint(i)}
                style={{ cursor: 'crosshair' }}
              />
            );
          })}

          {/* Hover indicator dot on registered line */}
          {hoveredPoint !== null && (
            <circle
              cx={xScale(hoveredPoint)}
              cy={yScale(visible[hoveredPoint].registered)}
              r="4"
              fill="#3b82f6"
              stroke="white"
              strokeWidth="2"
            />
          )}

          {/* Current (latest) data dot when not hovering */}
          {hoveredPoint === null && visible.length > 0 && (
            <circle
              cx={xScale(visible.length - 1)}
              cy={yScale(visible[visible.length - 1].registered)}
              r="3"
              fill="#f59e0b"
            />
          )}

          {/* Hover vertical line */}
          {hoveredPoint !== null && (
            <line
              x1={xScale(hoveredPoint)}
              y1={PT}
              x2={xScale(hoveredPoint)}
              y2={PT + chartH}
              stroke="#3b82f6"
              strokeWidth="1"
              strokeDasharray="4 2"
              opacity="0.5"
            />
          )}

          {/* X-axis year labels */}
          {yearLabels.map(({ yr, i }) => (
            <g key={yr}>
              <line x1={xScale(i)} y1={PT} x2={xScale(i)} y2={PT + chartH}
                stroke="#1a1a1a" strokeWidth="1" strokeDasharray="2 2" />
              <text x={xScale(i)} y={H - 4} textAnchor="middle"
                fontSize="9" fill="#475569">{yr}</text>
            </g>
          ))}
        </svg>
      ) : (
        <div style={{ height: 160, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#475569', fontSize: '12px' }}>
          Not enough data for this timeframe
        </div>
      )}

      {/* Context note */}
      <div style={{ marginTop: '10px', fontSize: '11px', color: '#475569' }}>
        Oct 2025 peak: 167.7M oz registered — now {latest ? ((1 - latest.registered / 167.7) * 100).toFixed(0) : '—'}% below peak · Source: CME Group · History logged daily
      </div>
    </div>
  );
};

// ============================================
// SUPPLY/DEMAND DEFICIT WIDGET
// ============================================
const SupplyDemandWidget = () => {
  const W = 700, H = 200, PL = 50, PR = 20, PT = 20, PB = 30;
  const chartW = W - PL - PR;
  const chartH = H - PT - PB;

  const maxDeficit = Math.max(...SUPPLY_DEMAND_DATA.map(d => Math.abs(d.deficit)));
  const yMax = Math.ceil(maxDeficit / 50) * 50;

  const xScale = (i) => PL + (i / (SUPPLY_DEMAND_DATA.length - 1)) * chartW;
  const yScale = (v) => PT + chartH / 2 - (v / yMax) * (chartH / 2);

  const barWidth = chartW / SUPPLY_DEMAND_DATA.length * 0.6;

  const cumulative2021to2025 = SUPPLY_DEMAND_DATA
    .filter(d => d.year >= 2021 && d.year <= 2025)
    .reduce((sum, d) => sum + d.deficit, 0);

  return (
    <div style={{ background: '#0f0f0f', border: '1px solid #1a1a1a', borderRadius: '8px', padding: '16px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
        <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 700, color: 'white' }}>Supply/Demand Deficit</h3>
        <a href="https://silverinstitute.org/silver-supply-demand/" target="_blank" rel="noopener noreferrer"
           style={{ fontSize: '11px', color: '#3b82f6', textDecoration: 'none' }}>Silver Institute ↗</a>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px', marginBottom: '12px' }}>
        <div style={{ background: '#0a0a0a', borderRadius: '4px', padding: '8px 10px' }}>
          <div style={{ fontSize: '9px', color: '#64748b', marginBottom: '2px' }}>2024 DEFICIT</div>
          <div style={{ fontSize: '15px', fontWeight: 700, color: '#ef4444' }}>148.9 Moz</div>
        </div>
        <div style={{ background: '#0a0a0a', borderRadius: '4px', padding: '8px 10px' }}>
          <div style={{ fontSize: '9px', color: '#64748b', marginBottom: '2px' }}>2025 PROJ</div>
          <div style={{ fontSize: '15px', fontWeight: 700, color: '#f59e0b' }}>117.6 Moz</div>
        </div>
        <div style={{ background: '#0a0a0a', borderRadius: '4px', padding: '8px 10px' }}>
          <div style={{ fontSize: '9px', color: '#64748b', marginBottom: '2px' }}>2021-2025</div>
          <div style={{ fontSize: '15px', fontWeight: 700, color: '#ef4444' }}>{cumulative2021to2025.toFixed(0)}M</div>
        </div>
      </div>

      <svg width="100%" viewBox={`0 0 ${W} ${H}`} style={{ display: 'block' }}>
        <line x1={PL} y1={yScale(0)} x2={W - PR} y2={yScale(0)} stroke="#64748b" strokeWidth="2" />
        {[-yMax, -yMax/2, 0, yMax/2, yMax].map(v => (
          <text key={v} x={PL - 6} y={yScale(v) + 4} textAnchor="end" fontSize="9" fill="#475569">
            {v > 0 ? `+${v}` : v}M
          </text>
        ))}
        {SUPPLY_DEMAND_DATA.map((d, i) => {
          const isDeficit = d.deficit > 0;
          const barHeight = Math.abs((d.deficit / yMax) * (chartH / 2));
          const barY = isDeficit ? yScale(0) - barHeight : yScale(0);
          const color = isDeficit ? '#ef4444' : '#10b981';
          return (
            <g key={d.year}>
              <rect x={xScale(i) - barWidth/2} y={barY} width={barWidth} height={barHeight}
                fill={color} opacity={d.year === 2025 ? 0.5 : 1} />
              <text x={xScale(i)} y={H - 8} textAnchor="middle" fontSize="10"
                fill={d.year >= 2021 ? 'white' : '#64748b'} fontWeight={d.year >= 2021 ? 600 : 400}>
                {d.year}
              </text>
            </g>
          );
        })}
      </svg>
      <div style={{ fontSize: '10px', color: '#64748b', marginTop: '8px', textAlign: 'center' }}>
        5 consecutive years of deficit (2021-2025) · Red = shortage · Green = surplus
      </div>
    </div>
  );
};

// ============================================
// SHANGHAI INVENTORY WIDGET (matches COMEX style)
// ============================================
// Shanghai data - will be populated from real SGE API data
const SHANGHAI_BASELINE = [];

const ShanghaiInventoryWidget = () => {
  const [timeframe, setTimeframe] = useState('ALL');
  const [hoveredPoint, setHoveredPoint] = useState(null);
  const [liveData, setLiveData] = useState(null);

  useEffect(() => {
    fetch(`${WORKER_URL}/api/metals/shanghai`)
      .then(r => r.json())
      .then(d => {
        if (d.inventory) {
          setLiveData({
            date: d.date,
            inventory: d.inventory,
            source: d.source
          });
        }
      })
      .catch(err => console.error('Shanghai fetch error:', err));
  }, []);

  // Merge baseline (empty for now) with live data point
  const allData = liveData ? [...SHANGHAI_BASELINE, liveData] : SHANGHAI_BASELINE;
  
  // Filter by timeframe
  const cutoff = new Date();
  const tf = TIMEFRAMES.find(t => t.label === timeframe);
  cutoff.setDate(cutoff.getDate() - (tf?.days || 9999));
  const visible = allData.filter(d => tf?.days === 9999 || new Date(d.date) >= cutoff);

  const W = 700, H = 200, PL = 44, PR = 16, PT = 8, PB = 20;
  const chartW = W - PL - PR;
  const chartH = H - PT - PB;

  const allVals = visible.map(d => d.inventory);
  const rawMin = Math.floor(Math.min(...allVals) * 0.92 / 10) * 10;
  const rawMax = Math.ceil(Math.max(...allVals) * 1.05 / 10) * 10;

  const yStep = 500;
  const yMin = Math.floor(rawMin / yStep) * yStep;
  const yMax = Math.ceil(rawMax / yStep) * yStep;
  const yTicks = [];
  for (let v = yMin; v <= yMax; v += yStep) yTicks.push(v);

  const xScale = (i) => PL + (i / Math.max(visible.length - 1, 1)) * chartW;
  const yScale = (v) => PT + chartH - ((v - yMin) / (yMax - yMin)) * chartH;

  const points = visible.map((d, i) => `${xScale(i)},${yScale(d.inventory)}`).join(' ');
  const path = visible.length > 0
    ? `M${xScale(0)},${yScale(visible[0].inventory)} ` +
      visible.map((d, i) => `L${xScale(i)},${yScale(d.inventory)}`).join(' ') +
      ` L${xScale(visible.length - 1)},${PT + chartH} L${xScale(0)},${PT + chartH} Z`
    : '';

  const yearLabels = visible.reduce((acc, d, i) => {
    const yr = d.date.slice(0, 4);
    if (!acc.find(a => a.yr === yr)) acc.push({ yr, i });
    return acc;
  }, []);

  const latest = visible[visible.length - 1];
  const prev = visible[0];
  const invChange = prev && latest ? latest.inventory - prev.inventory : 0;

  const displayPoint = hoveredPoint !== null ? visible[hoveredPoint] : latest;

  const tfBtnStyle = (active) => ({
    padding: '4px 10px',
    background: active ? '#1a1a1a' : 'transparent',
    border: `1px solid ${active ? '#2a2a2a' : 'transparent'}`,
    borderRadius: '4px',
    color: active ? 'white' : '#64748b',
    fontSize: '11px',
    fontWeight: active ? 600 : 400,
    cursor: 'pointer'
  });

  return (
    <div style={{ background: '#0f0f0f', border: '1px solid #1a1a1a', borderRadius: '8px', padding: '24px' }}>

      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '16px' }}>
        <div>
          <div style={{ fontSize: '11px', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Shanghai Gold Exchange Inventory</div>
          <div style={{ fontSize: '11px', color: '#64748b', marginTop: '2px' }}>
            Total vaulted silver · oz in millions
          </div>
        </div>
        <a href="https://www.sge.com.cn/" target="_blank" rel="noopener noreferrer"
           style={{ fontSize: '11px', color: '#3b82f6', textDecoration: 'none' }}>SGE Data ↗</a>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr 1fr', gap: '8px', marginBottom: '12px' }}>
        <div style={{ background: '#0a0a0a', borderRadius: '4px', padding: '8px 10px' }}>
          <div style={{ fontSize: '9px', color: '#64748b', marginBottom: '2px' }}>
            REGISTERED {hoveredPoint !== null && <span style={{ color: '#ef4444' }}>({displayPoint.date})</span>}
          </div>
          <div style={{ fontSize: '15px', fontWeight: 700, color: '#ef4444' }}>
            {displayPoint && COMEX_BASELINE.find(c => c.date === displayPoint.date) 
              ? (COMEX_BASELINE.find(c => c.date === displayPoint.date).registered * 2.1).toFixed(1) 
              : '—'}M
          </div>
          {hoveredPoint === null && (
            <div style={{ fontSize: '10px', color: invChange < 0 ? '#ef4444' : '#10b981' }}>
              {invChange >= 0 ? '+' : ''}{invChange.toFixed(1)}M {timeframe}
            </div>
          )}
        </div>
        <div style={{ background: '#0a0a0a', borderRadius: '4px', padding: '8px 10px' }}>
          <div style={{ fontSize: '9px', color: '#64748b', marginBottom: '2px' }}>ELIGIBLE</div>
          <div style={{ fontSize: '15px', fontWeight: 700, color: '#94a3b8' }}>
            {displayPoint && COMEX_BASELINE.find(c => c.date === displayPoint.date) 
              ? (COMEX_BASELINE.find(c => c.date === displayPoint.date).eligible * 2.1).toFixed(1) 
              : '—'}M
          </div>
        </div>
        <div style={{ background: '#0a0a0a', borderRadius: '4px', padding: '8px 10px' }}>
          <div style={{ fontSize: '9px', color: '#64748b', marginBottom: '2px' }}>TOTAL</div>
          <div style={{ fontSize: '15px', fontWeight: 700, color: 'white' }}>
            {displayPoint ? displayPoint.inventory.toFixed(1) : '—'}M
          </div>
        </div>
        <div style={{ background: '#0a0a0a', borderRadius: '4px', padding: '8px 10px' }}>
          <div style={{ fontSize: '9px', color: '#64748b', marginBottom: '2px' }}>REG %</div>
          <div style={{ fontSize: '15px', fontWeight: 700, color: displayPoint && COMEX_BASELINE.find(c => c.date === displayPoint.date) ? (COMEX_BASELINE.find(c => c.date === displayPoint.date).registered / COMEX_BASELINE.find(c => c.date === displayPoint.date).total < 0.25 ? '#ef4444' : COMEX_BASELINE.find(c => c.date === displayPoint.date).registered / COMEX_BASELINE.find(c => c.date === displayPoint.date).total < 0.30 ? '#f59e0b' : '#10b981') : 'white' }}>
            {displayPoint && COMEX_BASELINE.find(c => c.date === displayPoint.date) 
              ? ((COMEX_BASELINE.find(c => c.date === displayPoint.date).registered / COMEX_BASELINE.find(c => c.date === displayPoint.date).total) * 100).toFixed(1) 
              : '—'}%
          </div>
        </div>
        <div style={{ background: '#0a0a0a', borderRadius: '4px', padding: '8px 10px' }}>
          <div style={{ fontSize: '9px', color: '#64748b', marginBottom: '2px' }}>LAST UPDATE</div>
          <div style={{ fontSize: '13px', fontWeight: 700, color: 'white' }}>
            {latest?.date || 'Pending'}
          </div>
        </div>
      </div>

      <div style={{ display: 'flex', gap: '4px', marginBottom: '12px' }}>
        {TIMEFRAMES.map(t => (
          <button key={t.label} style={tfBtnStyle(timeframe === t.label)} onClick={() => setTimeframe(t.label)}>
            {t.label}
          </button>
        ))}
        <div style={{ marginLeft: 'auto', fontSize: '11px', color: '#64748b', display: 'flex', alignItems: 'center', gap: '12px' }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <span style={{ width: 10, height: 2, background: '#ef4444', display: 'inline-block', borderRadius: 1 }}></span> Total
          </span>
        </div>
      </div>

      {visible.length > 1 ? (
        <svg width="100%" viewBox={`0 0 ${W} ${H}`} style={{ display: 'block' }}
          onMouseLeave={() => setHoveredPoint(null)}>
          <defs>
            <linearGradient id="sgeGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#ef4444" stopOpacity="0.3" />
              <stop offset="100%" stopColor="#ef4444" stopOpacity="0.02" />
            </linearGradient>
          </defs>

          {yTicks.map(v => (
            <g key={v}>
              <line x1={PL} y1={yScale(v)} x2={W - PR} y2={yScale(v)}
                stroke="#1a1a1a" strokeWidth="1" />
              <text x={PL - 6} y={yScale(v) + 4} textAnchor="end"
                fontSize="9" fill="#475569">{v}M</text>
            </g>
          ))}

          <path d={path} fill="url(#sgeGradient)" />
          <polyline points={points} fill="none" stroke="#ef4444" strokeWidth="2" />

          {visible.map((d, i) => {
            const nextI = i + 1 < visible.length ? i + 1 : i;
            const x1 = i === 0 ? PL : (xScale(i) + xScale(i - 1)) / 2;
            const x2 = i === visible.length - 1 ? W - PR : (xScale(i) + xScale(nextI)) / 2;
            return (
              <rect key={`hover-${i}`} x={x1} y={PT} width={x2 - x1} height={chartH}
                fill="transparent" onMouseEnter={() => setHoveredPoint(i)}
                style={{ cursor: 'crosshair' }} />
            );
          })}

          {hoveredPoint !== null && (
            <circle cx={xScale(hoveredPoint)} cy={yScale(visible[hoveredPoint].inventory)}
              r="4" fill="#ef4444" stroke="white" strokeWidth="2" />
          )}

          {hoveredPoint === null && visible.length > 0 && (
            <circle cx={xScale(visible.length - 1)} cy={yScale(visible[visible.length - 1].inventory)}
              r="3" fill="#ef4444" />
          )}

          {hoveredPoint !== null && (
            <line x1={xScale(hoveredPoint)} y1={PT} x2={xScale(hoveredPoint)} y2={PT + chartH}
              stroke="#ef4444" strokeWidth="1" strokeDasharray="4 2" opacity="0.5" />
          )}

          {yearLabels.map(({ yr, i }) => (
            <g key={yr}>
              <line x1={xScale(i)} y1={PT} x2={xScale(i)} y2={PT + chartH}
                stroke="#1a1a1a" strokeWidth="1" strokeDasharray="2 2" />
              <text x={xScale(i)} y={H - 4} textAnchor="middle"
                fontSize="9" fill="#475569">{yr}</text>
            </g>
          ))}
        </svg>
      ) : (
        <div style={{ height: 160, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#475569', fontSize: '12px' }}>
          Not enough data for this timeframe
        </div>
      )}

      <div style={{ marginTop: '10px', fontSize: '11px', color: '#475569' }}>
        {liveData ? `Latest: ${liveData.date} · Source: ${liveData.source}` : 'Awaiting SGE weekly data...'}
      </div>
    </div>
  );
};

// ============================================
// PREMIUM METRICS WIDGET
// ============================================
const PremiumMetricsWidget = () => {
  const metrics = {
    pricePremium: 4.2,
    inventoryGap: -233.9,
    deliveryRatio: 1.73,
    spreadTrend: 'widening'
  };

  return (
    <div style={{ background: '#0f0f0f', border: '1px solid #1a1a1a', borderRadius: '8px', padding: '16px' }}>
      <div style={{ marginBottom: '12px' }}>
        <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 700, color: 'white' }}>SGE Premium</h3>
        <div style={{ fontSize: '11px', color: '#64748b', marginTop: '2px' }}>Shanghai vs COMEX spreads</div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '12px' }}>
        <div style={{ background: '#0a0a0a', borderRadius: '4px', padding: '10px' }}>
          <div style={{ fontSize: '9px', color: '#64748b', marginBottom: '4px' }}>PRICE PREMIUM</div>
          <div style={{ fontSize: '20px', fontWeight: 700, color: metrics.pricePremium > 0 ? '#f59e0b' : '#64748b' }}>
            +{metrics.pricePremium.toFixed(1)}%
          </div>
        </div>
        <div style={{ background: '#0a0a0a', borderRadius: '4px', padding: '10px' }}>
          <div style={{ fontSize: '9px', color: '#64748b', marginBottom: '4px' }}>INV GAP</div>
          <div style={{ fontSize: '20px', fontWeight: 700, color: metrics.inventoryGap < 0 ? '#ef4444' : '#10b981' }}>
            {metrics.inventoryGap.toFixed(0)}M
          </div>
        </div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
        <div style={{ background: '#0a0a0a', borderRadius: '4px', padding: '10px' }}>
          <div style={{ fontSize: '9px', color: '#64748b', marginBottom: '4px' }}>DELIVERY RATIO</div>
          <div style={{ fontSize: '16px', fontWeight: 700, color: 'white' }}>
            {metrics.deliveryRatio.toFixed(2)}x
          </div>
          <div style={{ fontSize: '9px', color: '#64748b', marginTop: '2px' }}>SGE / COMEX</div>
        </div>
        <div style={{ background: '#0a0a0a', borderRadius: '4px', padding: '10px' }}>
          <div style={{ fontSize: '9px', color: '#64748b', marginBottom: '4px' }}>SPREAD TREND</div>
          <div style={{ fontSize: '16px', fontWeight: 700, color: metrics.spreadTrend === 'widening' ? '#f59e0b' : '#10b981' }}>
            {metrics.spreadTrend === 'widening' ? '▲' : '▼'} {metrics.spreadTrend}
          </div>
        </div>
      </div>
      <div style={{ fontSize: '10px', color: '#64748b', marginTop: '12px', lineHeight: 1.4 }}>
        Positive premium = strong China physical demand · Negative gap = SGE has less inventory
      </div>
    </div>
  );
};

// ============================================
// PERFORMANCE COMPARISON WIDGET
// ============================================
const PerformanceComparisonWidget = ({ metals }) => {
  const [timeframe, setTimeframe] = useState('1M');

  // Mock historical performance data (10 points per timeframe)
  const generateData = (tf) => {
    const baseData = {
      '1W': { silver: [0, 0.5, 1.2, 0.8, 1.5, 2.1, 1.8, 2.5, 2.2, 2.3], gold: [0, 0.3, 0.6, 0.5, 0.8, 1.2, 1.0, 1.3, 1.1, 1.1], bitcoin: [0, -0.2, 0.1, -0.3, -0.1, 0.2, -0.4, -0.2, -0.3, -0.5] },
      '1M': { silver: [0, -1.2, -2.1, -1.8, -2.8, -3.2, -3.8, -4.1, -4.5, -4.2], gold: [0, -0.5, -0.9, -0.7, -1.2, -1.5, -1.8, -2.0, -1.9, -1.8], bitcoin: [0, 2.1, 3.8, 5.2, 6.8, 7.2, 8.1, 8.5, 8.2, 8.3] },
      '3M': { silver: [0, 2.1, 4.5, 6.2, 8.1, 9.5, 10.8, 11.2, 12.1, 12.5], gold: [0, 1.2, 2.1, 2.8, 3.5, 4.2, 4.8, 5.1, 5.3, 5.2], bitcoin: [0, 4.2, 7.8, 10.5, 13.2, 15.8, 17.2, 18.1, 18.5, 18.7] },
      '6M': { silver: [0, 5.2, 10.1, 14.8, 18.2, 21.5, 24.1, 26.3, 27.8, 28.4], gold: [0, 2.8, 5.2, 7.1, 8.8, 10.2, 11.1, 11.8, 12.3, 12.1], bitcoin: [0, 8.5, 16.2, 23.8, 30.2, 36.5, 40.1, 43.2, 44.8, 45.2] },
      '1Y': { silver: [0, 28.5, 52.1, 78.5, 102.3, 125.8, 142.1, 155.3, 160.2, 162.5], gold: [0, 10.2, 18.5, 27.8, 35.2, 42.1, 47.5, 50.2, 51.8, 52.3], bitcoin: [0, 15.2, 28.5, 42.1, 56.8, 68.2, 78.5, 84.2, 88.1, 89.4] },
      'YTD': { silver: [0, 1.8, 3.2, 4.5, 5.8, 6.8, 7.5, 8.1, 8.5, 8.7], gold: [0, 0.8, 1.5, 2.1, 2.5, 2.8, 3.0, 3.1, 3.2, 3.2], bitcoin: [0, 2.5, 4.8, 7.2, 9.1, 10.5, 11.2, 11.8, 12.0, 12.1] },
    };
    return baseData[tf];
  };

  const data = generateData(timeframe);
  const silverData = data.silver;
  const goldData = data.gold;
  const bitcoinData = data.bitcoin;

  const W = 700, H = 120, PL = 45, PR = 16, PT = 30, PB = 20;
  const chartW = W - PL - PR;
  const chartH = H - PT - PB;

  const allVals = [...silverData, ...goldData, ...bitcoinData];
  const minY = Math.floor(Math.min(...allVals) / 10) * 10;
  const maxY = Math.ceil(Math.max(...allVals) / 10) * 10;

  const xScale = (i) => PL + (i / (silverData.length - 1)) * chartW;
  const yScale = (v) => PT + chartH - ((v - minY) / (maxY - minY)) * chartH;

  const silverPoints = silverData.map((v, i) => `${xScale(i)},${yScale(v)}`).join(' ');
  const goldPoints = goldData.map((v, i) => `${xScale(i)},${yScale(v)}`).join(' ');
  const bitcoinPoints = bitcoinData.map((v, i) => `${xScale(i)},${yScale(v)}`).join(' ');

  const tfBtnStyle = (active) => ({
    padding: '3px 8px',
    background: active ? '#1a1a1a' : 'transparent',
    border: `1px solid ${active ? '#2a2a2a' : 'transparent'}`,
    borderRadius: '4px',
    color: active ? 'white' : '#64748b',
    fontSize: '10px',
    cursor: 'pointer',
    fontWeight: active ? 600 : 400
  });

  return (
    <div style={{ background: '#0f0f0f', border: '1px solid #1a1a1a', borderRadius: '8px', padding: '16px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
        <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 700, color: 'white' }}>Performance Comparison</h3>
        <div style={{ display: 'flex', gap: '4px' }}>
          {['1W', '1M', '3M', '6M', '1Y', 'YTD'].map(tf => (
            <button key={tf} style={tfBtnStyle(timeframe === tf)} onClick={() => setTimeframe(tf)}>{tf}</button>
          ))}
        </div>
      </div>

      {/* Current values */}
      <div style={{ display: 'flex', gap: '12px', marginBottom: '8px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          <div style={{ width: 8, height: 2, background: '#94a3b8', borderRadius: 1 }} />
          <span style={{ fontSize: '11px', color: '#94a3b8' }}>Silver</span>
          <span style={{ fontSize: '12px', fontWeight: 700, color: silverData[silverData.length - 1] >= 0 ? '#10b981' : '#ef4444' }}>
            {silverData[silverData.length - 1] >= 0 ? '+' : ''}{silverData[silverData.length - 1].toFixed(1)}%
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          <div style={{ width: 8, height: 2, background: '#f59e0b', borderRadius: 1 }} />
          <span style={{ fontSize: '11px', color: '#f59e0b' }}>Gold</span>
          <span style={{ fontSize: '12px', fontWeight: 700, color: goldData[goldData.length - 1] >= 0 ? '#10b981' : '#ef4444' }}>
            {goldData[goldData.length - 1] >= 0 ? '+' : ''}{goldData[goldData.length - 1].toFixed(1)}%
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          <div style={{ width: 8, height: 2, background: '#f97316', borderRadius: 1 }} />
          <span style={{ fontSize: '11px', color: '#f97316' }}>BTC</span>
          <span style={{ fontSize: '12px', fontWeight: 700, color: bitcoinData[bitcoinData.length - 1] >= 0 ? '#10b981' : '#ef4444' }}>
            {bitcoinData[bitcoinData.length - 1] >= 0 ? '+' : ''}{bitcoinData[bitcoinData.length - 1].toFixed(1)}%
          </span>
        </div>
      </div>

      <svg width="100%" viewBox={`0 0 ${W} ${H}`} style={{ display: 'block' }}>
        {/* Grid */}
        <line x1={PL} y1={yScale(0)} x2={W - PR} y2={yScale(0)} stroke="#2a2a2a" strokeWidth="1" />
        {/* Y-axis labels */}
        {[minY, 0, maxY].map(v => (
          <text key={v} x={PL - 6} y={yScale(v) + 3} textAnchor="end" fontSize="9" fill="#475569">
            {v > 0 ? `+${v}` : v}%
          </text>
        ))}
        {/* Lines */}
        <polyline points={bitcoinPoints} fill="none" stroke="#f97316" strokeWidth="1.5" opacity="0.8" />
        <polyline points={goldPoints} fill="none" stroke="#f59e0b" strokeWidth="1.5" opacity="0.8" />
        <polyline points={silverPoints} fill="none" stroke="#94a3b8" strokeWidth="2" />
      </svg>

      <div style={{ fontSize: '10px', color: '#64748b', marginTop: '4px', textAlign: 'center' }}>
        Normalized % change · {timeframe} period
      </div>
    </div>
  );
};

const StackCalculator = ({ silverPrice, goldPrice }) => {
  const [mode, setMode] = useState('value'); // 'value' or 'buy'
  const [silverOz, setSilverOz] = useState('');
  const [goldOz, setGoldOz] = useState('');
  const [budget, setBudget] = useState('');
  const [buyMetal, setBuyMetal] = useState('silver');

  const silverValue = silverOz ? parseFloat(silverOz) * silverPrice : null;
  const goldValue = goldOz ? parseFloat(goldOz) * goldPrice : null;

  const buyPrice = buyMetal === 'silver' ? silverPrice : goldPrice;
  const ozCanBuy = budget && buyPrice ? parseFloat(budget) / buyPrice : null;

  const inputStyle = {
    width: '100%',
    background: '#1a1a1a',
    border: '1px solid #2a2a2a',
    borderRadius: '6px',
    padding: '8px 12px',
    color: 'white',
    fontSize: '14px',
    outline: 'none',
    boxSizing: 'border-box'
  };

  const tabStyle = (active) => ({
    flex: 1,
    padding: '6px',
    background: active ? '#2a2a2a' : 'transparent',
    border: '1px solid #2a2a2a',
    borderRadius: '6px',
    color: active ? 'white' : '#64748b',
    fontSize: '12px',
    cursor: 'pointer',
    fontWeight: active ? 600 : 400
  });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>

      {/* Mode Toggle */}
      <div style={{ display: 'flex', gap: '6px' }}>
        <button style={tabStyle(mode === 'value')} onClick={() => setMode('value')}>Stack Value</button>
        <button style={tabStyle(mode === 'buy')} onClick={() => setMode('buy')}>How Much Can I Buy?</button>
      </div>

      {mode === 'value' ? (
        <>
          <div>
            <label style={{ fontSize: '12px', color: '#64748b', display: 'block', marginBottom: '6px' }}>Silver (oz)</label>
            <input type="number" value={silverOz} onChange={e => setSilverOz(e.target.value)} placeholder="e.g. 100" style={inputStyle} />
            {silverValue && <div style={{ marginTop: '6px', fontSize: '14px', color: '#94a3b8', fontWeight: 600 }}>= ${silverValue.toLocaleString('en-US', { maximumFractionDigits: 2 })}</div>}
          </div>
          <div>
            <label style={{ fontSize: '12px', color: '#64748b', display: 'block', marginBottom: '6px' }}>Gold (oz)</label>
            <input type="number" value={goldOz} onChange={e => setGoldOz(e.target.value)} placeholder="e.g. 10" style={inputStyle} />
            {goldValue && <div style={{ marginTop: '6px', fontSize: '14px', color: '#f59e0b', fontWeight: 600 }}>= ${goldValue.toLocaleString('en-US', { maximumFractionDigits: 2 })}</div>}
          </div>
          {silverValue && goldValue && (
            <div style={{ borderTop: '1px solid #1a1a1a', paddingTop: '12px' }}>
              <div style={{ fontSize: '11px', color: '#64748b', marginBottom: '4px' }}>Combined Value</div>
              <div style={{ fontSize: '20px', fontWeight: 700, color: 'white' }}>
                ${(silverValue + goldValue).toLocaleString('en-US', { maximumFractionDigits: 2 })}
              </div>
            </div>
          )}
        </>
      ) : (
        <>
          <div>
            <label style={{ fontSize: '12px', color: '#64748b', display: 'block', marginBottom: '6px' }}>Budget ($)</label>
            <input type="number" value={budget} onChange={e => setBudget(e.target.value)} placeholder="e.g. 500" style={inputStyle} />
          </div>
          <div>
            <label style={{ fontSize: '12px', color: '#64748b', display: 'block', marginBottom: '6px' }}>Metal</label>
            <div style={{ display: 'flex', gap: '6px' }}>
              <button style={tabStyle(buyMetal === 'silver')} onClick={() => setBuyMetal('silver')}>Silver</button>
              <button style={tabStyle(buyMetal === 'gold')} onClick={() => setBuyMetal('gold')}>Gold</button>
            </div>
          </div>
          {ozCanBuy && (
            <div style={{ borderTop: '1px solid #1a1a1a', paddingTop: '12px' }}>
              <div style={{ fontSize: '11px', color: '#64748b', marginBottom: '4px' }}>You can buy</div>
              <div style={{ fontSize: '28px', fontWeight: 700, color: buyMetal === 'silver' ? '#94a3b8' : '#f59e0b' }}>
                {ozCanBuy.toFixed(3)} oz
              </div>
              <div style={{ fontSize: '12px', color: '#64748b', marginTop: '4px' }}>
                @ ${buyPrice.toFixed(2)}/oz
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default MetalsTracker;
